import { useParams, Link } from "react-router-dom";
import { ShieldCheck, FileBadge, Cog, Timer, ArrowRight } from "lucide-react";
import { getService } from "../data/services";
import { Seo, serviceSchema, faqSchema, breadcrumbSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { FaqAccordion } from "../components/FaqAccordion";
import { EstimateForm } from "../components/EstimateForm";
import { CtaBlock } from "../components/CtaBlock";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Img } from "../components/Img";
import { LAST_UPDATED, BIZ, cleanText } from "../data/site";
import NotFound from "./NotFound";

const WHY = [
  { icon: ShieldCheck, t: "I-CAR Gold Class", d: "The highest training tier in the collision industry — held by roughly one shop in ten." },
  { icon: FileBadge, t: "Written Lifetime Warranty", d: "Workmanship and paint guaranteed in writing for as long as you own the vehicle." },
  { icon: Cog, t: "Computerized Frame Machine", d: "Laser-measured structural verification with printed before/after data." },
  { icon: Timer, t: "Time-Efficient Paint Booth", d: "Controlled cure cycles deliver factory finish on a faster schedule." },
];

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <NotFound />;

  const path = `/services/${s.slug}`;
  const schemas = [
    serviceSchema(s),
    faqSchema(s.faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
      { name: s.menuName, path },
    ]),
  ];
  const related = s.related.map((r) => getService(r)).filter(Boolean);

  return (
    <article data-testid={`service-page-${s.slug}`}>
      <Seo title={s.metaTitle} description={s.metaDesc} path={path} schemas={schemas} />

      <section className="relative pt-32 lg:pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 surface-weave opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: s.menuName, path }]} />
            <Eyebrow>{s.badge}</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance">
              {s.h1}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">{s.answer}</p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className={`clip-frame overflow-hidden rounded-sm border border-white/10 bg-surface ${s.imgNarrow ? "flex justify-center" : ""}`}>
              <Img name={s.img} alt={s.alt} eager className={s.imgNarrow ? "h-64 sm:h-80 w-auto max-w-full" : "h-64 sm:h-80 w-full object-cover"} sizes="(min-width: 1024px) 40vw, 100vw" />
            </div>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {s.sections.map((sec, i) => (
            <Reveal key={sec.q} className={i > 0 ? "mt-14" : ""}>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white text-balance">{sec.q}</h2>
              <p className="mt-4 text-sm sm:text-base text-slate-200 leading-relaxed"><strong className="text-body font-semibold">{cleanText(sec.lead)}</strong></p>
              <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">{cleanText(sec.body)}</p>
              {i === 0 && s.bodyImg && (
                <div className="clip-frame mt-8 overflow-hidden rounded-sm border border-white/10">
                  <Img name={s.bodyImg} alt={s.bodyAlt || s.alt} className="block mx-auto h-64 w-full object-cover" sizes="(min-width: 1024px) 60vw, 100vw" />
                </div>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 lg:py-24 surface-panel border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Step By Step</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
              Our {s.menuName} Process
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {s.process.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-surface p-6">
                  <span className="font-display text-3xl font-extrabold accent-text">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-base font-semibold text-slate-100">{p.t}</h3>
                  <p className="mt-2 text-xs sm:text-sm text-slate-400 leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-white/[0.08] bg-ink/60 p-6 transition-colors duration-500 hover:border-accent/30">
                  <w.icon size={24} className="text-accent" strokeWidth={1.6} aria-hidden="true" />
                  <h3 className="mt-3 font-display text-base font-semibold text-slate-100">{w.t}</h3>
                  <p className="mt-2 text-xs sm:text-sm text-slate-400 leading-relaxed">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10">
            <Eyebrow>Straight Answers</Eyebrow>
            <h2 className="mt-4 font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
              {s.menuName} FAQs
            </h2>
          </Reveal>
          <FaqAccordion faqs={s.faqs} idPrefix={`faq-${s.slug}`} />
        </div>
      </section>

      <section className="pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-8">
            <Eyebrow>Related Services</Eyebrow>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {related.map((r) => (
              <Reveal key={r.slug}>
                <Link
                  to={`/services/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group flex h-full items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-surface p-6 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1"
                >
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-accent/80">{r.badge}</span>
                    <h3 className="mt-1.5 font-display text-lg font-semibold text-slate-100 group-hover:text-accent transition-colors">{r.menuName}</h3>
                  </div>
                  <ArrowRight size={18} className="shrink-0 text-mute group-hover:text-accent group-hover:translate-x-1 transition-all" aria-hidden="true" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-white/[0.08] bg-surface p-7 sm:p-10">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Get a Free {s.menuName} Estimate
              </h2>
              <p className="mt-3 text-sm sm:text-base text-slate-400">
                Send photos and a quick description — a master technician responds, usually within one business hour. Prefer to talk? Call{" "}
                <a href={BIZ.phoneHref} className="text-accent hover:text-accentdeep transition-colors">{BIZ.phoneDisplay}</a>.
              </p>
              <div className="mt-8">
                <EstimateForm compact testid={`estimate-${s.slug}`} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBlock />
          <p className="mt-8 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>
    </article>
  );
}
